import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import AppBar from '@mui/material/AppBar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import DoorHandleSVG from '../components/svg/DoorHandleSVG';
import DoNotDisturbSVG from '../components/svg/DoNotDisturbSVG';
import FileSVG from '../components/svg/FileSVG';
import LinkSVG from '../components/svg/LinkSVG';
import CloudSVG from '../components/svg/CloudSVG';
import PhoneSVG from '../components/svg/PhoneSVG';
import SubscriptionCard from '../components/subsription-card/SubscriptionCard';
import LandingIcon from '../components/landing-icon/LandingIcon';
import Login from '../components/log-in/Login';
import ExternalEntryCodeForm from '../components/entry-code/ExternalEntryCodeForm';
import UseLocalStorage from '../Util/custom-hooks/UseLocalStorage';

const minWidth = 300;

export default function MiniDrawer() {
    // The room id this page was reached at, when it is standing in for a
    // presentation room the visitor is not logged in for (`/presentation/:roomId`).
    // Read it from the route rather than persisting it: a stored id outlives the
    // visit that produced it and would send a later guest to the wrong room.
    const { roomId: directURL } = useParams();

    const [elevated, setElevated] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [showJoin, setShowJoin] = useState(!!directURL);
    const [entryCode, setEntryCode] = UseLocalStorage('external-entry-code', '');

    // The router reuses this component across `/welcome` and
    // `/presentation/:roomId` - they are sibling routes rendering the same
    // element - so moving between them changes directURL without remounting,
    // and an initial-render capture would go stale. Re-sync on the room id
    // alone, which leaves a manual close of the dialog standing.
    useEffect(() => setShowJoin(!!directURL), [directURL]);

    function closeLoginDialog() {
        setShowLogin(false);
        setShowJoin(false);
    }

    useEffect(() => {
        function setElevation() {
            const header = document.querySelector('header');
            setElevated(header.getBoundingClientRect().top < 1);
        }
        window.addEventListener('scroll', setElevation);
        return () => window.removeEventListener('scroll', setElevation);
    }, []);

    return (
        <Grid container direction='column' sx={{ minWidth }}>
            <Grid
                container
                alignItems='center'
                sx={{
                    width: '100%',
                    minHeight: '17vh',
                    height: '100%',
                    maxHeight: '65vh',
                    background: 'white',
                    paddingTop: '1em',
                    position: 'sticky',
                    top: 0,
                    zIndex: -1,
                    minWidth
                }}>
                <Grid container justifyContent='center' alignItems='center'>
                    <Box
                        component='img'
                        src='/images/laptop-sample.png'
                        alt='computer'
                        sx={{
                            height: 'auto',
                            width: '80%',
                            minWidth: '320px',
                            maxWidth: 'calc(65vh * 1.75)',
                            maxHeight: '65vh'
                        }}></Box>
                </Grid>
            </Grid>
            <div>
                <AppBar
                    elevation={elevated ? 8 : 0}
                    position='static'
                    sx={{
                        '&.MuiPaper-elevation0': {
                            background: 'white'
                        },
                        flexGrow: 1,
                        padding: '1em',
                        transition: 'linear 0.5s',
                        position: 'sticky',
                        top: '-1px',
                        minWidth
                    }}>
                    <Grid container justifyContent='space-around'>
                        <Grid item xs={3}>
                            <Grid container alignContent='center' justifyContent='center'>
                                {' '}
                                <Box pl={1}>
                                    <Typography variant='h4'>VYU1</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid item xs>
                            <Grid
                                container
                                alignItems='center'
                                alignContent='center'
                                justifyContent='flex-end'
                                spacing={2}>
                            <Grid item>
                                <Button
                                    variant='outlined'
                                    size='small'
                                    color={elevated ? 'secondary' : 'primary'}
                                    onClick={() => setShowLogin(true)}>
                                    Login
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant='contained'
                                    size='small'
                                    color={elevated ? 'secondary' : 'primary'}
                                    onClick={() => setShowJoin(true)}>
                                    Join
                                </Button>
                            </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </AppBar>
                <Grid
                    container
                    alignItems='center'
                    alignContent='center'
                    justifyContent='center'
                    style={{ width: '100%', background: 'white' }}>
                    <Grid item>
                        <ExternalEntryCodeForm
                            onSubmit={(ec) => {
                                setEntryCode(ec);
                                setShowLogin(true);
                            }}
                        />
                    </Grid>
                </Grid>

                {/* This container holds everything below the title bar */}
                <Box
                    component='div'
                    sx={{
                        minWidth,
                        // width: '100vw',
                        height: '100%',
                        background: 'white',
                        position: 'relative'
                    }}>
                    <Container
                        maxwidth
                        sx={(theme) => ({
                            minHeight: '110vh',
                            background: 'white',
                            width: '100%',
                            padding: theme.spacing(2)
                        })}>
                        <Grid
                            container
                            item
                            justifyContent='center'
                            sx={(theme) => ({
                                minHeight: '300px',
                                transition: 'ease-in-out 0.2s',
                                '& h1': {
                                    textShadow: `2px 2px ${theme.palette.secondary.main}`,
                                    // Restored from the original v4 down('xs') (below 600px); v5's
                                    // down('xs') never matches, so use down('sm') to keep the intent.
                                    [theme.breakpoints.down('sm')]: {
                                        fontSize: '4em'
                                    }
                                },
                                '& h6': {
                                    fontSize: '1.6em',
                                    fontWeight: 100,
                                    // Same down('xs') -> down('sm') restoration as above.
                                    [theme.breakpoints.down('sm')]: {
                                        fontSize: '1em'
                                    }
                                }
                            })}>
                            <Box py={2}>
                                <Grid container justifyContent='center' alignItems='center'>
                                    <Grid item>
                                        <Grid container justifyContent='center' direction='column'>
                                            <Typography variant='h1' align='center'>
                                                VYU1
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={10}>
                                        <Grid container justifyContent='center'>
                                            <Typography
                                                variant='subtitle2'
                                                align='center'>
                                                Persistent hosted presentation rooms to communicate a
                                                shared vision. VYU1 allows you to create interactive
                                                spaces to discuss ideas and designs.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>

                        {/* Icon Grid */}
                        <Grid container justifyContent='center'>
                            <Grid container item justifyContent='center' spacing={3} md={11}>
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    sx={{ width: '100%', minWidth: '200px', maxWidth: '400px' }}>
                                    <LandingIcon
                                        title='Rooms'
                                        text='Create personalized rooms to meet and collaborate with others'>
                                        <DoorHandleSVG />
                                    </LandingIcon>
                                </Grid>
                                <Grid
                                    item
                                    sm={12}
                                    md={6}
                                    sx={{ width: '100%', minWidth: '200px', maxWidth: '400px' }}>
                                    <LandingIcon
                                        title='Convenience'
                                        text='Use either public or private links to instantly share and invite others'>
                                        <LinkSVG />
                                    </LandingIcon>
                                </Grid>
                                <Grid
                                    item
                                    sm={12}
                                    md={6}
                                    sx={{ width: '100%', minWidth: '200px', maxWidth: '400px' }}>
                                    <LandingIcon
                                        title='Privacy'
                                        text='Control who has access to your rooms and your content'>
                                        <DoNotDisturbSVG />
                                    </LandingIcon>
                                </Grid>
                                <Grid
                                    item
                                    sm={12}
                                    md={6}
                                    sx={{ width: '100%', minWidth: '200px', maxWidth: '400px' }}>
                                    <LandingIcon
                                        title='Flexibility'
                                        text='Combine documents of various formats into a cohesive presentation'>
                                        <FileSVG />
                                    </LandingIcon>
                                </Grid>
                                <Grid
                                    item
                                    sm={12}
                                    md={6}
                                    sx={{ width: '100%', minWidth: '200px', maxWidth: '400px' }}>
                                    <LandingIcon
                                        title='Accessibility'
                                        text='Your rooms and data are accessible anywhere, anytime you need it'>
                                        <CloudSVG />
                                    </LandingIcon>
                                </Grid>
                                <Grid
                                    item
                                    sm={12}
                                    md={6}
                                    sx={{ width: '100%', minWidth: '200px', maxWidth: '400px' }}>
                                    <LandingIcon
                                        title='Simplicity'
                                        text='Join a presentation and contribute from any computer or device'>
                                        <PhoneSVG />
                                    </LandingIcon>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Subscription Grid */}
                        <Grid
                            container
                            justifyContent='center'
                            spacing={2}
                            sx={{ margin: '50px 0' }}>
                            <Grid
                                item
                                sm={12}
                                md
                                sx={{ padding: '1em', minWidth: 320, maxWidth: 400 }}>
                                <SubscriptionCard
                                    title='Starter Package'
                                    price={{ amount: 'Free' }}
                                    table={[
                                        { key: 'Rooms', value: '2' },
                                        { key: 'Participants', value: '15' },
                                        { key: 'Presentation (each)', value: '10 Slides' },
                                        { key: 'Storage (total)', value: '250 MB' }
                                    ]}
                                />
                            </Grid>

                            <Grid
                                item
                                sm={12}
                                md
                                sx={{ padding: '1em', minWidth: 320, maxWidth: 400 }}>
                                <SubscriptionCard
                                    title='5-Room Package'
                                    price={{ amount: '$9', frequency: 'monthly' }}
                                    table={[
                                        { key: 'Rooms', value: '5' },
                                        { key: 'Participants', value: '100' },
                                        { key: 'Presentation (each)', value: '50 Slides' },
                                        { key: 'Storage (total)', value: '5 GB' }
                                    ]}
                                />
                            </Grid>

                            <Grid
                                item
                                sm={12}
                                md
                                sx={{ padding: '1em', minWidth: 320, maxWidth: 400 }}>
                                <SubscriptionCard
                                    title='By the Room'
                                    price={{ amount: '$2', frequency: 'per-room, monthly' }}
                                    table={[
                                        { key: 'Additional Room', value: '1' },
                                        { key: 'Participants', value: '100' },
                                        { key: 'Presentation (each)', value: '50 Slides' },
                                        { key: 'Additional Storage', value: '500 MB' }
                                    ]}
                                />
                            </Grid>
                        </Grid>
                        <Login
                            open={showLogin || showJoin}
                            onClose={closeLoginDialog}
                            tab={showLogin ? 0 : 1}
                            externalEntryCode={entryCode}
                            directURL={directURL}
                        />
                    </Container>
                </Box>
            </div>
        </Grid>
    );
}
